import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { fetchStaffs, updateStaff } from '../../utils/supabaseClient';
import StaffForm from './StaffForm';
import Notification from '../common/Notification';
import '../../styles/Staffs.css';
import { useAuth } from '../../contexts/AuthContext';

function Staffs() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [staffs, setStaffs] = useState([]);
  const [filteredStaffs, setFilteredStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('Teacher');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchColumn, setSearchColumn] = useState('all');
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(50);
  const [columns, setColumns] = useState([]);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [currentStaffAction, setCurrentStaffAction] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // Define which columns to display and in what order
  // This maps database field names to display names
  const columnDisplayMap = {
    name: 'Name',
    role: 'Role',
    gender: 'Gender',
    qualification: 'Qualification',
    subject_specialization: 'Subject',
    mobile: 'Phone',
    email: 'Email',
    date_of_joining: 'Joining Date',
    address: 'Address',
    dob: 'Date of Birth',
    aadhaar_no: 'Aadhaar Number',
    fathers_aadhaar: "Father's Aadhaar",
    mothers_aadhaar: "Mother's Aadhaar",
    salary: 'Salary',
    is_active: 'Active Status'
  };

  useEffect(() => {
    const loadStaffs = async () => {
      setLoading(true);
      const data = await fetchStaffs();
      
      // Extract all unique column names from the data
      if (data && data.length > 0) {
        const firstStaff = data[0];
        const availableColumns = Object.keys(firstStaff)
          .filter(key => key !== 'created_at' && key !== 'updated_at')
          .filter(key => isAdmin || key !== 'salary')
          .sort((a, b) => {
            // Put important fields first
            const priority = ['staff_no', 'name', 'role', 'gender', 'qualification', 'subject_specialization', 'mobile', 'email'];
            const aIndex = priority.indexOf(a);
            const bIndex = priority.indexOf(b);
            
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            return a.localeCompare(b);
          });
        setColumns(availableColumns);
      }
      
      const staffsWithDefaults = data.map(staff => ({
        ...staff,
        id: staff.staff_no, // map staff_no to id
        active: staff.is_active !== null ? staff.is_active : true
      }));
      
      setStaffs(staffsWithDefaults);
      setFilteredStaffs(staffsWithDefaults);
      setLoading(false);
    };

    loadStaffs();
  }, []);

  // Handle adding or updating a staff
  const handleAddStaff = (newStaff) => {
    console.log('Received staff data in handleAddStaff:', newStaff);
    
    if (!newStaff) {
      console.error('No staff data received in handleAddStaff');
      showNotification('Failed to process staff data', 'error');
      return;
    }
    
    const staffToAdd = {
      ...newStaff,
      // Use staff_no as the primary identifier, not id
      staff_no: newStaff.staff_no,
      role: newStaff.role || 'Teacher',
      is_active: newStaff.is_active !== null ? newStaff.is_active : true,
      active: newStaff.is_active !== null ? newStaff.is_active : true, // Keep for UI compatibility
    };
    
    console.log('Processed staff data:', staffToAdd);
    
    try {
      // If we're editing, replace the existing staff
      if (editingStaff) {
        const staffNo = editingStaff.staff_no;
        console.log('Updating staff with staff_no:', staffNo);
        
        setStaffs(prevStaffs => 
          prevStaffs.map(staff => 
            staff.staff_no === staffNo ? staffToAdd : staff
          )
        );
        
        setFilteredStaffs(prev => 
          prev.map(staff => 
            staff.staff_no === staffNo ? staffToAdd : staff
          )
        );

        showNotification('Staff updated successfully', 'success');
      } else {
        // Otherwise add the new staff to the beginning of the list
        setStaffs(prevStaffs => [staffToAdd, ...prevStaffs]);
        setFilteredStaffs(prev => [staffToAdd, ...prev]);
        showNotification('Staff added successfully', 'success');
      }

      // Reset editing state
      setEditingStaff(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error processing staff data:', error);
      showNotification('Failed to process staff data', 'error');
    }
  };

  // No delete staff functionality needed

  // Handle editing a staff
  const handleEditStaff = (staffNo) => {
    const staffToEdit = staffs.find(staff => staff.staff_no === staffNo);
    if (staffToEdit) {
      setEditingStaff(staffToEdit);
      setShowAddForm(true);
    }
  };

  // Show notification using the common Notification component
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification({ message: '', type: '' });
  };

  // Show status confirmation modal
  const showStatusConfirmation = (staffNo) => {
    closeAllModals();

    const staff = staffs.find(t => t.staff_no === staffNo);
    if (!staff) return;

    const action = staff.is_active ? 'deactivate' : 'activate';
    setCurrentStaffAction({
      id: staffNo,
      action,
      staff,
      staffName: staff.name || 'this staff'
    });
    setShowStatusModal(true);
  };

  // Handle status confirmation
  const handleStatusConfirm = async () => {
    if (!currentStaffAction) return;

    const { id, action } = currentStaffAction;
    const newActiveStatus = action === 'activate';

    try {
      setIsUpdatingStatus(true);
      console.log(`Updating staff ${id} status to ${newActiveStatus ? 'active' : 'inactive'}`);

      // Update in Supabase - use is_active field, not active
      await updateStaff(id, { is_active: newActiveStatus });

      // Update UI - maintain both active and is_active for consistency
      setStaffs(prevStaffs =>
        prevStaffs.map(staff =>
          staff.staff_no === id
            ? { ...staff, active: newActiveStatus, is_active: newActiveStatus }
            : staff
        )
      );

      setFilteredStaffs(prev =>
        prev.map(staff =>
          staff.staff_no === id
            ? { ...staff, active: newActiveStatus, is_active: newActiveStatus }
            : staff
        )
      );

      // Show success message
      showNotification(
        `Staff ${newActiveStatus ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification(`Failed to ${action} staff: ${error.message}`, 'error');
    } finally {
      setIsUpdatingStatus(false);
      setShowStatusModal(false);
      setCurrentStaffAction(null);
    }
  };

  // Close all modals
  const closeAllModals = () => {
    setShowStatusModal(false);
    setShowAddForm(false);
    setCurrentStaffAction(null);
    setEditingStaff(null);
  };

  // Handle export with options
  const handleExport = (activeOnly) => {
    setShowExportOptions(false);
    exportToExcel(activeOnly);
  };

  // Close export options when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showExportOptions && !e.target.closest('.export-container')) {
        setShowExportOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportOptions]);

  const columnOptions = [
    { value: 'all', label: 'All Columns' },
    { value: 'name', label: 'Name' },
    { value: 'gender', label: 'Gender' },
    { value: 'qualification', label: 'Qualification' },
    { value: 'subject', label: 'Subject' },
    { value: 'experience', label: 'Experience' },
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'aadhar', label: 'Aadhar' },
    { value: 'address', label: 'Address' },
    { value: 'dateOfJoining', label: 'Date of Joining' }
  ];

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const exportToExcel = (activeOnly = false) => {
    // Filter staffs based on the activeOnly flag
    const staffsToExport = activeOnly
      ? sortedStaffs.filter(staff => staff.active)
      : sortedStaffs;

    const dataToExport = staffsToExport.map(staff => ({
      'Name': staff.name,
      'Gender': staff.gender,
      'Qualification': staff.qualification,
      'Subject': staff.subject,
      'Experience': staff.experience,
      'Phone': staff.phone,
      'Email': staff.email,
      'Aadhar': staff.aadhar,
      'Address': staff.address,
      'Date of Joining': staff.dateOfJoining,
      'Status': staff.active ? 'Active' : 'Inactive'
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Convert the data to a worksheet
    const ws = XLSX.utils.json_to_sheet(dataToExport);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Staffs');

    // Generate the Excel file and trigger download
    const fileName = `staffs_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Apply sorting to filtered staffs
  const sortedStaffs = React.useMemo(() => {
    const sorted = [...filteredStaffs].sort((a, b) => {
      if (a[sortField] === null) return 1;
      if (b[sortField] === null) return -1;
      if (a[sortField] === b[sortField]) return 0;

      const aValue = typeof a[sortField] === 'string' ? a[sortField].toLowerCase() : a[sortField];
      const bValue = typeof b[sortField] === 'string' ? b[sortField].toLowerCase() : b[sortField];

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    return sorted;
  }, [filteredStaffs, sortField, sortDirection]);

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = sortedStaffs.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(sortedStaffs.length / recordsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Filter staffs when search term, column, or role changes
  useEffect(() => {
    let filtered = staffs;
    
    // Apply role filter
    if (roleFilter !== 'All') {
      filtered = filtered.filter(staff => staff.role === roleFilter);
    }
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(staff => {
        if (searchColumn === 'all') {
          return Object.values(staff).some(val =>
            String(val).toLowerCase().includes(searchLower)
          );
        }
        const columnValue = staff[searchColumn];
        return columnValue ? String(columnValue).toLowerCase().includes(searchLower) : false;
      });
    }
    
    setFilteredStaffs(filtered);
    setCurrentPage(1);
  }, [searchTerm, searchColumn, staffs, roleFilter]);

  // Pagination component
  const renderPagination = () => {
    // Always show pagination even with one page so it shows the record count

    const pageNumbers = [];
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        {totalPages > 1 && (
          <>
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className="page-nav"
              aria-label="First page"
            >
              &laquo;
            </button>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="page-nav"
              aria-label="Previous page"
            >
              &lsaquo;
            </button>

            {startPage > 1 && (
              <span className="page-ellipsis">...</span>
            )}

            {pageNumbers.map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`page-number ${currentPage === number ? 'active' : ''}`}
                aria-label={`Page ${number}`}
              >
                {number}
              </button>
            ))}

            {endPage < totalPages && (
              <span className="page-ellipsis">...</span>
            )}

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="page-nav"
              aria-label="Next page"
            >
              &rsaquo;
            </button>
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className="page-nav"
              aria-label="Last page"
            >
              &raquo;
            </button>
          </>
        )}

        <div className="pagination-info">
          {sortedStaffs.length > 0 ? (
            <>Showing {indexOfFirstRecord + 1} - {Math.min(indexOfLastRecord, sortedStaffs.length)} of {sortedStaffs.length} records</>
          ) : (
            <>No records to display</>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ textAlign: 'center', padding: '50px', fontSize: '1.2rem' }}>
        Loading Staffs...
      </div>
    );
  }

  return (
    <div className="staffs-container">
      <div className="staffs-header">

        <h1>Staff Management</h1>
      </div>

      {/* Role Filter Tabs */}
      <div className="role-filter-tabs" style={{ marginBottom: '20px', display: 'flex', gap: '10px', borderBottom: '2px solid #e5e7eb', paddingBottom: '10px' }}>
        {['Teacher', 'Peon', 'Guard', 'Driver', 'All'].map(role => (
          <button
            key={role}
            onClick={() => setRoleFilter(role)}
            className={`role-tab ${roleFilter === role ? 'active' : ''}`}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderBottom: roleFilter === role ? '3px solid #4361ee' : '3px solid transparent',
              background: 'none',
              cursor: 'pointer',
              fontWeight: roleFilter === role ? '600' : '400',
              color: roleFilter === role ? '#4361ee' : '#6b7280',
              transition: 'all 0.2s'
            }}
          >
            {role}
          </button>
        ))}
      </div>

      <div className="staffs-controls">
        <div className="search-container">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
            />
            <i className="fas fa-search"></i>
          </div>
          <div className="column-dropdown">
            <button
              className="column-selector"
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              aria-label="Select search column"
            >
              <i className="fas fa-columns"></i>
              <span>{columnOptions.find(col => col.value === searchColumn)?.label || 'All Columns'}</span>
              <i className={`fas fa-chevron-${showColumnDropdown ? 'up' : 'down'}`}></i>
            </button>
            {showColumnDropdown && (
              <div className="dropdown-menu">
                {columnOptions.map(option => (
                  <div
                    key={option.value}
                    className={`dropdown-item ${searchColumn === option.value ? 'active' : ''}`}
                    onClick={() => {
                      setSearchColumn(option.value);
                      setShowColumnDropdown(false);
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="button-group">
          <div className="export-container" style={{ position: 'relative' }}>
            <button
              className="export-btn"
              onClick={() => setShowExportOptions(!showExportOptions)}
            >
              <i className="fas fa-file-export"></i> Export to Excel
            </button>
            {showExportOptions && (
              <div className="export-options" style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '200px'
              }}>
                <div
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    backgroundColor: '#fff',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                  onClick={() => handleExport(false)}
                >
                  Export All Staffs
                </div>
                <div
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                  onClick={() => handleExport(true)}
                >
                  Export Active Staffs Only
                </div>
              </div>
            )}
          </div>
          <button
            className="add-staff-btn"
            onClick={() => setShowAddForm(true)}
          >
            <i className="fas fa-plus"></i> Add New Staff
          </button>
        </div>
      </div>

      <div className="staffs-table-container">
        <table className="staffs-table">
          <thead>
            <tr>
              <th>Actions</th>
              {columns.map(column => {
                // Skip staff_no as it's used as ID
                if (column === 'staff_no') return null;

                // Skip is_active as it's shown via toggle button
                if (column === 'is_active') return null;

                const displayName = columnDisplayMap[column] || column;
                return (
                  <th
                    key={column}
                    onClick={() => handleSort(column)}
                    className={`sortable ${sortField === column ? 'sorted-' + sortDirection : ''}`}
                  >
                    {displayName} <i className="fas fa-sort"></i>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {currentRecords.length > 0 ? (
              currentRecords.map((staff, index) => (
                <tr key={staff.staff_no} className={!staff.active ? 'inactive' : ''}>
                  <td className="action-buttons">
                    <button
                      type="button"
                      title={staff.active ? 'Active (click to deactivate)' : 'Inactive (click to activate)'}
                      onClick={() => showStatusConfirmation(staff.staff_no)}
                      className={`status-toggle ${staff.active ? 'active' : 'inactive'}`}
                      disabled={isUpdatingStatus}
                    >
                      <i className={`fas ${staff.active ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                    </button>
                    <button
                      type="button"
                      className="edit-btn"
                      title="Edit"
                      onClick={() => handleEditStaff(staff.staff_no)}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    {/* Delete button removed as per requirement */}
                  </td>
                  {columns.map(column => {
                    // Skip staff_no as it's used as ID
                    if (column === 'staff_no') return null;

                    // Skip is_active as it's shown via toggle button
                    if (column === 'is_active') return null;

                    // Format the value based on column type
                    let value = staff[column];

                    // Format date fields
                    if (column === 'dob' || column === 'date_of_joining') {
                      if (value) {
                        try {
                          const date = new Date(value);
                          if (!isNaN(date)) {
                            value = date.toLocaleDateString();
                          }
                        } catch (e) {
                          // Keep original value if date parsing fails
                        }
                      }
                    }

                    // Format number fields
                    if (column === 'mobile' || column === 'aadhaar_no') {
                      value = value?.toString() || '';
                    }

                    return <td key={column}>{value}</td>;
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="no-records">
                  No staffs found matching your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* Add/Edit Staff Form Modal */}
      {showAddForm && (
        <StaffForm
          onAddStaff={handleAddStaff}
          onClose={() => {
            setShowAddForm(false);
            setEditingStaff(null);
          }}
          editStaff={editingStaff}
          isAdmin={isAdmin}
        />
      )}

      {/* Notification */}
      {notification.message && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={handleNotificationClose}
        />
      )}

      {/* Status Confirmation Modal */}
      {showStatusModal && currentStaffAction && (
        <div className="modal-overlay" onClick={closeAllModals}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Staff Details - {currentStaffAction.staffName || 'N/A'}</h3>
              <button className="student-close-btn" onClick={closeAllModals}>&times;</button>
            </div>

            <div className="staff-details-grid">
              <div className="detail-item">
                <span className="detail-label">Name:</span>
                <span className="detail-value">{currentStaffAction.name || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Subject:</span>
                <span className="detail-value">{currentStaffAction.subject_specialization || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{currentStaffAction.mobile || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`status-badge ${currentStaffAction.active ? 'active' : 'inactive'}`}>
                  {currentStaffAction.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="confirmation-message">
              Are you sure you want to {currentStaffAction.action}
              <strong> {currentStaffAction.staffName}</strong>?
              <div className="action-note">
                {currentStaffAction.action === 'deactivate'
                  ? 'This will mark the staff as inactive.'
                  : 'This will mark the staff as active.'}
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeAllModals}
                disabled={isUpdatingStatus}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${currentStaffAction.action === 'activate' ? 'btn-success' : 'btn-warning'}`}
                onClick={handleStatusConfirm}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Updating...
                  </>
                ) : (
                  `Yes, ${currentStaffAction.action}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Staffs;
